import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { API_PATHS } from "@/lib/api-paths";
import { API_BASE_URL } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ApiResponse {
  status: number;
  data: unknown;
  ok: boolean;
}

function ResponseBlock({ resp }: { resp: ApiResponse | null }) {
  const [copied, setCopied] = useState(false);
  if (!resp) return null;
  const ok = resp.ok;
  const text = typeof resp.data === "string" ? resp.data : JSON.stringify(resp.data, null, 2);
  const copyToClipboard = () => {
    void navigator.clipboard.writeText(`HTTP ${resp.status}\n${text}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className={`mt-3 relative rounded border p-3 text-sm font-mono ${ok ? "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950" : "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950"}`}>
      <button onClick={copyToClipboard} className="absolute top-2 right-2 text-xs opacity-60 hover:opacity-100" aria-label="Copy">
        {copied ? "Copied!" : "Copy"}
      </button>
      <div className="mb-1 font-semibold text-xs opacity-70">HTTP {resp.status}</div>
      <pre className="whitespace-pre-wrap break-all text-xs max-h-[400px] overflow-auto">{text}</pre>
    </div>
  );
}

function SectionCard({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <span className="text-xs text-muted-foreground">{open ? "▼" : "▶"}</span>
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
}

type TccTabId = "login" | "master-data" | "documents" | "operations" | "callback";

const SAMPLE_SAVE_CO = {
  requestBy: "3792",
  docPriority: "5",
  declarator: "PACIFIC SUGAR CORPORATION LIMITED",
  declaratorId: 1614,
  companyId: 5426,
  consignorName: "PACIFIC SUGAR CORPORATION LIMITED",
  consignor: "PACIFIC SUGAR CORPORATION LIMITED",
  consignorId: 1614,
  liveSignature: "N",
  sigBranchId: 1,
  certificateDateRequest: "approve_date",
  consigneeName: "GLOBAL TRADING CO., LTD.",
  vesselType: "1",
  vesselNo: "MV PACIFIC STAR V.123",
  loadingDate: "2026-05-20",
  destination: "MALAYSIA",
  destinationCountry: "MY",
  invoiceChk: "Y",
  countryOfOrigin: "TH",
  docType: "CO",
  signAuthorizedId: 154387,
  isPackRq: "N",
  isRqstatus: 4,
  invoice: [
    { invoiceId: 0, docHid: 0, invoiceNum: 1, invoiceNo: "INV-2026-001", invoiceDate: "2026-05-19", isActive: "Y", requestBy: 3792 },
  ],
  items: [
    { docItemId: 0, itemNo: 1, harmonizedCode: "17019910", markChk: "N", markItem: null, descriptionChk: "Y", description: "REFINED WHITE SUGAR ICUMSA 45", quantity: 500, quantityChk: "Y", quantityUnit: "BAGS", quantityUnitCode: 401, weight: 25000, weightChk: "Y", weightUnit: "KGS.", weightUnitCode: "KGS", grossWeight: 25500, grossWeightChk: "Y", grossWeightUnit: "KGS.", grossWeightUnitCode: "KGS", exportValue: 15000, exchangeRate: 34.11, exchangeValue: 511650, docHeaderId: 0, isActive: "Y" },
  ],
  attachments: [
    { attachPicId: 0, attachFilePic: "https://fastly.picsum.photos/id/870/200/300.jpg?blur=2&grayscale&hmac=ujRymp644uYVjdKJM7kyLDSsrqNSMVRPnGU99cKl6Vs", attachType: 1, attachIndex: 1, remark: "INVOICE", attachHeaderType: 1, isActive: "Y", packId: 0 },
  ],
  isActive: "Y",
  isExternalRequest: "Y",
  callbackEndpoint: "https://your-callback-url.com/api/messageFromCo",
};

const SAMPLE_SAVE_CF = {
  companyId: 5426,
  certificateDateRequest: "approve_date",
  documentDate: "2026-05-19",
  destination: "INDONESIA",
  docSubTitleId: 28,
  documentSubjectTitleType: "General",
  documentReferenceNo: "REF-CF-001",
  totalPages: 3,
  book: "Y",
  liveSignature: "N",
  branchId: 1,
  isActive: "Y",
  docPriority: "5",
  consignorId: 1614,
  declaratorId: 5426,
  consignor: "PACIFIC SUGAR CORPORATION LIMITED",
  declarator: "DO FOOD CO.,LTD.",
  sigBranchId: 1,
  isRqstatus: 4,
  requestBy: "3792",
  attachments: [
    { attachPicId: 0, attachFilePic: "https://fastly.picsum.photos/id/870/200/300.jpg?blur=2&grayscale&hmac=ujRymp644uYVjdKJM7kyLDSsrqNSMVRPnGU99cKl6Vs", attachType: 10, attachIndex: 1, remark: "FREE SALE CERT.", attachHeaderType: 2, isActive: "Y", packId: 0 },
  ],
  isExternalRequest: "Y",
  callbackEndpoint: "https://your-callback-url.com/api/messageFromCo",
};

const SAMPLE_MESSAGE_FROM_CO = {
  packId: 0,
  remark: "ตรวจสอบผ่านเรียบร้อย",
  rqStatus: 5,
  approveType: "Y",
  markList: [],
  certificateList: [
    { docId: 0, certificateNo: "CO/260000001", seq: 1, paymentRefNo: "PAY001", receiptNo: "REC001" },
  ],
};

export function TccSimulationPage({ token }: { token: string | null }) {
  const [tccToken, setTccToken] = useState<string | null>(null);
  const [tccEmail, setTccEmail] = useState("admin");
  const [tccPassword, setTccPassword] = useState("admin");
  const [loginResp, setLoginResp] = useState<ApiResponse | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const [tab, setTab] = useState<TccTabId>("login");

  const [masterDataResp, setMasterDataResp] = useState<ApiResponse | null>(null);
  const [masterDataLoading, setMasterDataLoading] = useState(false);

  const [saveCoBody, setSaveCoBody] = useState(() => JSON.stringify(SAMPLE_SAVE_CO, null, 2));
  const [saveCoResp, setSaveCoResp] = useState<ApiResponse | null>(null);
  const [saveCoLoading, setSaveCoLoading] = useState(false);

  const [saveCfBody, setSaveCfBody] = useState(() => JSON.stringify(SAMPLE_SAVE_CF, null, 2));
  const [saveCfResp, setSaveCfResp] = useState<ApiResponse | null>(null);
  const [saveCfLoading, setSaveCfLoading] = useState(false);

  const [getPackId, setGetPackId] = useState("");
  const [getCoResp, setGetCoResp] = useState<ApiResponse | null>(null);
  const [getCoLoading, setGetCoLoading] = useState(false);

  const [getCfResp, setGetCfResp] = useState<ApiResponse | null>(null);
  const [getCfLoading, setGetCfLoading] = useState(false);

  const [listDocsResp, setListDocsResp] = useState<ApiResponse | null>(null);
  const [listDocsLoading, setListDocsLoading] = useState(false);

  const [cancelPackId, setCancelPackId] = useState("");
  const [cancelResp, setCancelResp] = useState<ApiResponse | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [deletePackId, setDeletePackId] = useState("");
  const [deleteResp, setDeleteResp] = useState<ApiResponse | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [queuePriority, setQueuePriority] = useState("5");
  const [queueResp, setQueueResp] = useState<ApiResponse | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);

  const [previewPackId, setPreviewPackId] = useState("");
  const [previewResp, setPreviewResp] = useState<ApiResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [paymentPackId, setPaymentPackId] = useState("");
  const [paymentResp, setPaymentResp] = useState<ApiResponse | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [errorMarkPackId, setErrorMarkPackId] = useState("");
  const [errorMarkResp, setErrorMarkResp] = useState<ApiResponse | null>(null);
  const [errorMarkLoading, setErrorMarkLoading] = useState(false);

  const [msgBody, setMsgBody] = useState(() => JSON.stringify(SAMPLE_MESSAGE_FROM_CO, null, 2));
  const [msgResp, setMsgResp] = useState<ApiResponse | null>(null);
  const [msgLoading, setMsgLoading] = useState(false);

  const [msgLog, setMsgLog] = useState<unknown[]>([]);
  const [msgLogLoading, setMsgLogLoading] = useState(false);

  const authHeaders: Record<string, string> = tccToken ? { Authorization: `Bearer ${tccToken}` } : {};

  const tccPost = useCallback(async (path: string, body: unknown): Promise<ApiResponse> => {
    try {
      const res = await fetch(`${API_BASE_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      return { status: res.status, data, ok: res.ok };
    } catch (err) {
      return { status: 0, data: `Network error: ${err instanceof Error ? err.message : "Unknown"}`, ok: false };
    }
  }, [tccToken]);

  const tccGet = useCallback(async (path: string): Promise<ApiResponse> => {
    try {
      const res = await fetch(`${API_BASE_URL}${path}`, {
        method: "GET",
        headers: authHeaders,
      });
      const contentType = res.headers.get("content-type");
      const data = contentType?.includes("json") ? await res.json() : await res.text();
      return { status: res.status, data, ok: res.ok };
    } catch (err) {
      return { status: 0, data: `Network error: ${err instanceof Error ? err.message : "Unknown"}`, ok: false };
    }
  }, [tccToken]);

  const handleLogin = async () => {
    setLoginLoading(true);
    const res = await api.post(API_PATHS.TCC_LOGIN, { body: { username: tccEmail, password: tccPassword } });
    setLoginResp(res);
    setLoginLoading(false);
    if (res.ok && (res.data as any)?.key) {
      setTccToken((res.data as any).key);
    }
  };

  const fetchMasterData = async (path: string) => {
    setMasterDataLoading(true);
    const res = await api.post(path, { token: tccToken, body: {} });
    setMasterDataResp(res);
    setMasterDataLoading(false);
  };

  const fetchListDocs = async () => {
    setListDocsLoading(true);
    const res = await api.post(API_PATHS.TCC_LIST_DOCUMENTS, {
      token: tccToken,
      body: {
        uId: 3792,
        gId: 2,
        pagination: { Skip: 0, Take: 20 },
      },
    });
    setListDocsResp(res);
    setListDocsLoading(false);
  };

  const fetchMsgLog = useCallback(async () => {
    setMsgLogLoading(true);
    const res = await api.get(API_PATHS.TCC_MESSAGE_LOG, { token });
    setMsgLogLoading(false);
    if (res.ok && Array.isArray(res.data)) {
      setMsgLog(res.data);
    }
  }, [token]);

  useEffect(() => {
    if (tab === "callback") fetchMsgLog();
  }, [tab, fetchMsgLog]);

  const sendCallback = async () => {
    let body: unknown;
    try {
      body = JSON.parse(msgBody);
    } catch {
      setMsgResp({ status: 0, data: "Invalid JSON", ok: false });
      return;
    }
    setMsgLoading(true);
    const res = await api.post(API_PATHS.TCC_MESSAGE_FROM_CO, { token, body });
    setMsgResp(res);
    setMsgLoading(false);
    fetchMsgLog();
  };

  const TABS: { id: TccTabId; label: string }[] = [
    { id: "login", label: "TCC Login" },
    { id: "master-data", label: "Master Data" },
    { id: "documents", label: "CO/CF Documents" },
    { id: "operations", label: "Operations" },
    { id: "callback", label: "Callback (messageFromCo)" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground">TCC CO Online Simulation</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Mock Thai Chamber of Commerce CO Online APIs — Certificate of Origin (CO) & Certificate of Free Sale (CF).
        </p>
        {tccToken && (
          <div className="mt-2 flex items-center gap-2 text-xs font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-md border border-emerald-500/20 inline-flex">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            TCC Token Active
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border pb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-t px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === t.id
                ? "bg-primary/15 text-foreground border-b-2 border-primary"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "login" && (
        <div className="space-y-4">
          <SectionCard title="Login to TCC CO Online" defaultOpen>
            <p className="mb-3 text-xs text-muted-foreground">
               Authenticates against <code>POST /tcc-co-api/api/logintcc</code>. Returns a JWT token used for all other TCC endpoints.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Email / Username</label>
                <input type="text" className="w-full rounded border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" value={tccEmail} onChange={(e) => setTccEmail(e.target.value)} placeholder="admin" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Password</label>
                <input type="password" className="w-full rounded border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" value={tccPassword} onChange={(e) => setTccPassword(e.target.value)} placeholder="admin" />
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={handleLogin} disabled={loginLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {loginLoading ? "Logging in…" : "Login"}
              </button>
            </div>
            <ResponseBlock resp={loginResp} />
          </SectionCard>

          <SectionCard title="Get User Profile">
            <p className="mb-3 text-xs text-muted-foreground">
               <code>POST /tcc-co-api/api/getUserProfile</code> — Returns full user profile with company info.
            </p>
            <button onClick={async () => { setMasterDataLoading(true); const r = await api.post(API_PATHS.TCC_GET_USER_PROFILE, { token: tccToken, body: {} }); setMasterDataResp(r); setMasterDataLoading(false); }} disabled={!tccToken || masterDataLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {masterDataLoading ? "Loading…" : "Get User Profile"}
            </button>
            <ResponseBlock resp={masterDataResp} />
          </SectionCard>
        </div>
      )}

      {tab === "master-data" && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Click any button to fetch master data. All endpoints use POST unless noted (GET for attach types).</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[
              { label: "Attach Types (GET)", path: API_PATHS.TCC_GET_LIST_ATTACH_TYPE, method: "GET" },
              { label: "Branches", path: API_PATHS.TCC_GET_LIST_BRANCH },
              { label: "Countries", path: API_PATHS.TCC_GET_LIST_COUNTRY },
              { label: "Vessel Types", path: API_PATHS.TCC_GET_LIST_VESSEL_TYPE },
              { label: "Relation Companies", path: API_PATHS.TCC_GET_RELATION_COMPANY },
              { label: "Declarator CO", path: API_PATHS.TCC_GET_DECLARATOR_CO },
              { label: "User Address", path: API_PATHS.TCC_GET_USER_ADDRESS },
              { label: "Signatories", path: API_PATHS.TCC_GET_LIST_NAME_LASTNAME },
              { label: "Quantity Units", path: API_PATHS.TCC_GET_LIST_UNIT },
              { label: "Weight Units", path: API_PATHS.TCC_GET_LIST_WEIGHT },
              { label: "Exchange Rate", path: API_PATHS.TCC_EXCHANGE_RATE_MAX_DATE },
              { label: "Harmonized Codes", path: API_PATHS.TCC_GET_HARMONIZED_CODE },
              { label: "Document Types", path: API_PATHS.TCC_GET_LIST_DOC },
              { label: "Status List", path: API_PATHS.TCC_GET_MASTER_STATUS_LIST },
            ].map((item) => (
              <button
                key={item.path}
                onClick={async () => {
                  setMasterDataLoading(true);
                  const r = item.method === "GET"
                    ? await api.get(item.path, { token: tccToken })
                    : await api.post(item.path, { token: tccToken, body: {} });
                  setMasterDataResp(r);
                  setMasterDataLoading(false);
                }}
                disabled={!tccToken || masterDataLoading}
                className="rounded border border-border px-3 py-2 text-xs font-medium hover:bg-accent disabled:opacity-50 text-left"
              >
                {item.label}
                <span className="block text-[10px] text-muted-foreground font-mono">{item.path}</span>
              </button>
            ))}
          </div>
          <ResponseBlock resp={masterDataResp} />
        </div>
      )}

      {tab === "documents" && (
        <div className="space-y-4">
          <SectionCard title="Save CO Document" defaultOpen>
            <p className="mb-3 text-xs text-muted-foreground">
              <code>POST /tcc-co-api/api/CO/saveCO</code> — Create or update a Certificate of Origin. Set <code>isRqstatus: 3</code> for draft, <code>4</code> to submit.
            </p>
            <textarea rows={16} className="w-full rounded border border-input bg-background px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring" value={saveCoBody} onChange={(e) => setSaveCoBody(e.target.value)} />
            <div className="mt-2 flex gap-2">
              <button onClick={async () => { let b; try { b = JSON.parse(saveCoBody); } catch { setSaveCoResp({ status: 0, data: "Invalid JSON", ok: false }); return; }               setSaveCoLoading(true); const r = await api.post(API_PATHS.TCC_SAVE_CO, { token: tccToken, body: b }); setSaveCoResp(r); setSaveCoLoading(false); }} disabled={!tccToken || saveCoLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {saveCoLoading ? "Saving…" : "Save CO"}
              </button>
            </div>
            <ResponseBlock resp={saveCoResp} />
            {saveCoResp?.ok && (saveCoResp.data as any)?.items?.draftCo && (
              <div className="mt-2">
                <a href={(saveCoResp.data as any).items.draftCo} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200">
                  📄 View Draft CO PDF
                </a>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Save CF Document" defaultOpen>
            <p className="mb-3 text-xs text-muted-foreground">
              <code>POST /tcc-co-api/api/saveCF</code> — Create or update a Certificate of Free Sale.
            </p>
            <textarea rows={14} className="w-full rounded border border-input bg-background px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring" value={saveCfBody} onChange={(e) => setSaveCfBody(e.target.value)} />
            <div className="mt-2 flex gap-2">
              <button onClick={async () => { let b; try { b = JSON.parse(saveCfBody); } catch { setSaveCfResp({ status: 0, data: "Invalid JSON", ok: false }); return; }               setSaveCfLoading(true); const r = await api.post(API_PATHS.TCC_SAVE_CF, { token: tccToken, body: b }); setSaveCfResp(r); setSaveCfLoading(false); }} disabled={!tccToken || saveCfLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {saveCfLoading ? "Saving…" : "Save CF"}
              </button>
            </div>
            <ResponseBlock resp={saveCfResp} />
          </SectionCard>

          <SectionCard title="Get CO / CF Document Detail">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Pack ID</label>
                <input type="text" className="w-full rounded border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" value={getPackId} onChange={(e) => setGetPackId(e.target.value)} placeholder="e.g. 5000000" />
              </div>
              <button onClick={async () => { if (!getPackId) return;               setGetCoLoading(true); const r = await api.post(API_PATHS.TCC_GET_CO, { token: tccToken, body: { packId: parseInt(getPackId) } }); setGetCoResp(r); setGetCoLoading(false); }} disabled={!tccToken || !getPackId || getCoLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {getCoLoading ? "Loading…" : "Get CO"}
              </button>
              <button onClick={async () => { if (!getPackId) return; setGetCfLoading(true); const r = await api.post(API_PATHS.TCC_GET_CF_DETAIL, { token: tccToken, body: { packId: parseInt(getPackId) } }); setGetCfResp(r); setGetCfLoading(false); }} disabled={!tccToken || !getPackId || getCfLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {getCfLoading ? "Loading…" : "Get CF"}
              </button>
            </div>
            {getCoResp && <div className="mt-2"><p className="text-xs font-medium text-muted-foreground mb-1">CO Response:</p><ResponseBlock resp={getCoResp} /></div>}
            {getCfResp && <div className="mt-2"><p className="text-xs font-medium text-muted-foreground mb-1">CF Response:</p><ResponseBlock resp={getCfResp} /></div>}
            {(getCoResp?.ok && (getCoResp.data as any)?.items?.attachments?.length > 0) && (
              <div className="mt-2">
                <p className="text-xs font-medium text-muted-foreground mb-1">Attachment Preview:</p>
                <div className="flex flex-wrap gap-2">
                  {(getCoResp.data as any).items.attachments.map((a: any, i: number) => (
                    <a key={i} href={a.attachFilePic} target="_blank" rel="noopener noreferrer">
                      <img src={a.attachFilePic} alt={`Attachment ${i + 1}`} className="h-20 w-auto rounded border border-border" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="List Documents" defaultOpen>
            <p className="mb-3 text-xs text-muted-foreground">
               <code>POST /tcc-co-api/api/getListDocumentCOCFExternal</code> — List all CO/CF documents.
            </p>
            <button onClick={fetchListDocs} disabled={!tccToken || listDocsLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {listDocsLoading ? "Loading…" : "Fetch Documents"}
            </button>
            <ResponseBlock resp={listDocsResp} />
            {listDocsResp?.ok && (listDocsResp.data as any)?.items?.data && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="pb-2 pr-3">Pack ID</th>
                      <th className="pb-2 pr-3">Type</th>
                      <th className="pb-2 pr-3">Request No</th>
                      <th className="pb-2 pr-3">Status</th>
                      <th className="pb-2 pr-3">Company</th>
                      <th className="pb-2 pr-3">Certificate</th>
                      <th className="pb-2 pr-3">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {((listDocsResp.data as any).items.data as any[]).map((d, i) => (
                      <tr key={i} className="border-b border-border/50 last:border-0">
                        <td className="py-1.5 pr-3 font-mono">{d.packId}</td>
                        <td className="py-1.5 pr-3"><span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${d.docType === "CO" ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" : "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"}`}>{d.docType}</span></td>
                        <td className="py-1.5 pr-3 font-mono text-[11px]">{d.requestNo || "—"}</td>
                        <td className="py-1.5 pr-3 text-[11px]">{d.isRqStatus}</td>
                        <td className="py-1.5 pr-3 text-[11px] max-w-[150px] truncate">{d.companyName}</td>
                        <td className="py-1.5 pr-3 font-mono text-[11px]">{d.certificateNo?.join(", ") || "—"}</td>
                        <td className="py-1.5 text-muted-foreground text-[11px]">{new Date(d.createDate).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {tab === "operations" && (
        <div className="space-y-4">
          <SectionCard title="Cancel Verification" defaultOpen>
            <p className="mb-3 text-xs text-muted-foreground">              <code>POST /tcc-co-api/api/cancelVerify</code> — Pull back document from verification queue.</p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Pack ID</label>
                <input type="text" className="w-full rounded border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" value={cancelPackId} onChange={(e) => setCancelPackId(e.target.value)} placeholder="e.g. 5000000" />
              </div>
              <button onClick={async () => { if (!cancelPackId) return; setCancelLoading(true); const r = await api.post(API_PATHS.TCC_CANCEL_VERIFY, { token: tccToken, body: { packId: parseInt(cancelPackId) } }); setCancelResp(r); setCancelLoading(false); }} disabled={!tccToken || !cancelPackId || cancelLoading} className="rounded bg-amber-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50">
                {cancelLoading ? "Cancelling…" : "Cancel Verify"}
              </button>
            </div>
            <ResponseBlock resp={cancelResp} />
          </SectionCard>

          <SectionCard title="Delete Document">
            <p className="mb-3 text-xs text-muted-foreground">              <code>POST /tcc-co-api/api/deleteCO</code> — Delete a CO/CF document request.</p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Pack ID</label>
                <input type="text" className="w-full rounded border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" value={deletePackId} onChange={(e) => setDeletePackId(e.target.value)} placeholder="e.g. 5000000" />
              </div>
              <button onClick={async () => { if (!deletePackId) return; setDeleteLoading(true); const r = await api.post(API_PATHS.TCC_DELETE_CO, { token: tccToken, body: { packId: parseInt(deletePackId) } }); setDeleteResp(r); setDeleteLoading(false); }} disabled={!tccToken || !deletePackId || deleteLoading} className="rounded bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {deleteLoading ? "Deleting…" : "Delete"}
              </button>
            </div>
            <ResponseBlock resp={deleteResp} />
          </SectionCard>

          <SectionCard title="Get Queue Count">
            <p className="mb-3 text-xs text-muted-foreground">              <code>POST /tcc-co-api/api/getQueueCount</code></p>
            <div className="flex items-end gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Priority</label>
                <select className="rounded border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" value={queuePriority} onChange={(e) => setQueuePriority(e.target.value)}>
                  <option value="4">4 (Urgent)</option>
                  <option value="5">5 (Normal)</option>
                </select>
              </div>
              <button onClick={async () => { setQueueLoading(true); const r = await api.post(API_PATHS.TCC_GET_QUEUE_COUNT, { token: tccToken, body: { queuePriority } }); setQueueResp(r); setQueueLoading(false); }} disabled={!tccToken || queueLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {queueLoading ? "Loading…" : "Get Queue Count"}
              </button>
            </div>
            <ResponseBlock resp={queueResp} />
          </SectionCard>

          <SectionCard title="Preview CO Pack PDF">
            <p className="mb-3 text-xs text-muted-foreground">              <code>POST /tcc-co-api/api/getPreviewCOPack</code> — Get draft CO PDF URL.</p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Pack ID</label>
                <input type="text" className="w-full rounded border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" value={previewPackId} onChange={(e) => setPreviewPackId(e.target.value)} placeholder="e.g. 5000000" />
              </div>
              <button onClick={async () => { if (!previewPackId) return; setPreviewLoading(true); const r = await api.post(API_PATHS.TCC_GET_PREVIEW_CO_PACK, { token: tccToken, body: { packId: parseInt(previewPackId) } }); setPreviewResp(r); setPreviewLoading(false); }} disabled={!tccToken || !previewPackId || previewLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {previewLoading ? "Loading…" : "Preview"}
              </button>
            </div>
            <ResponseBlock resp={previewResp} />
            {previewResp?.ok && (previewResp.data as any)?.items?.draftCoUrl && (
              <div className="mt-2">
                <a href={(previewResp.data as any).items.draftCoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200">
                  📄 Open Draft CO PDF
                </a>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Get Payment List">
            <p className="mb-3 text-xs text-muted-foreground">              <code>POST /tcc-co-api/api/paymentListGetByPackId</code></p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Pack ID</label>
                <input type="text" className="w-full rounded border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" value={paymentPackId} onChange={(e) => setPaymentPackId(e.target.value)} placeholder="e.g. 5000000" />
              </div>
              <button onClick={async () => { if (!paymentPackId) return; setPaymentLoading(true); const r = await api.post(API_PATHS.TCC_PAYMENT_LIST, { token: tccToken, body: { packId: parseInt(paymentPackId) } }); setPaymentResp(r); setPaymentLoading(false); }} disabled={!tccToken || !paymentPackId || paymentLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {paymentLoading ? "Loading…" : "Get Payments"}
              </button>
            </div>
            <ResponseBlock resp={paymentResp} />
          </SectionCard>

          <SectionCard title="Get Error Marks">
            <p className="mb-3 text-xs text-muted-foreground">              <code>POST /tcc-co-api/api/getErrorMark</code> — Get verification error marks for a document.</p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Pack ID</label>
                <input type="text" className="w-full rounded border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" value={errorMarkPackId} onChange={(e) => setErrorMarkPackId(e.target.value)} placeholder="e.g. 5000000" />
              </div>
              <button onClick={async () => { if (!errorMarkPackId) return; setErrorMarkLoading(true); const r = await api.post(API_PATHS.TCC_GET_ERROR_MARK, { token: tccToken, body: { packId: parseInt(errorMarkPackId) } }); setErrorMarkResp(r); setErrorMarkLoading(false); }} disabled={!tccToken || !errorMarkPackId || errorMarkLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {errorMarkLoading ? "Loading…" : "Get Error Marks"}
              </button>
            </div>
            <ResponseBlock resp={errorMarkResp} />
          </SectionCard>

          <SectionCard title="Upload Batch Image">
            <p className="mb-3 text-xs text-muted-foreground">              <code>POST /tcc-co-api/api/uploadBatchImage</code> — Returns a mock image URL.</p>
            <button onClick={async () => { setMasterDataLoading(true); const r = await api.post(API_PATHS.TCC_UPLOAD_BATCH_IMAGE, { token: tccToken, body: {} }); setMasterDataResp(r); setMasterDataLoading(false); }} disabled={!tccToken || masterDataLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {masterDataLoading ? "Uploading…" : "Upload Mock Image"}
            </button>
            <ResponseBlock resp={masterDataResp} />
            {masterDataResp?.ok && (masterDataResp.data as any)?.items?.attachFilePic && (
              <div className="mt-2">
                <p className="text-xs font-medium text-muted-foreground mb-1">Mock Image:</p>
                <a href={(masterDataResp.data as any).items.attachFilePic} target="_blank" rel="noopener noreferrer">
                  <img src={(masterDataResp.data as any).items.attachFilePic} alt="Mock upload" className="h-24 w-auto rounded border border-border" />
                </a>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Log Print History">
            <p className="mb-3 text-xs text-muted-foreground">              <code>POST /tcc-co-api/api/LogPrintHistory</code></p>
            <button onClick={async () => { setMasterDataLoading(true); const r = await api.post(API_PATHS.TCC_LOG_PRINT_HISTORY, { token: tccToken, body: { packId: 5000000, name: "ประภาส เจริญยิ่ง" } }); setMasterDataResp(r); setMasterDataLoading(false); }} disabled={!tccToken || masterDataLoading} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {masterDataLoading ? "Logging…" : "Log Print"}
            </button>
            <ResponseBlock resp={masterDataResp} />
          </SectionCard>
        </div>
      )}

      {tab === "callback" && (
        <div className="space-y-4">
          <SectionCard title="Send messageFromCo Callback" defaultOpen>
            <p className="mb-3 text-xs text-muted-foreground">
               <code>POST /tcc-co-api/api/messageFromCo</code> — Simulates CO system sending a status callback. Updates the document status in the mock store.
              Set <code>packId</code> to an existing document's packId to update it.
            </p>
            <textarea rows={14} className="w-full rounded border border-input bg-background px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring" value={msgBody} onChange={(e) => setMsgBody(e.target.value)} />
            <div className="mt-2 flex gap-2">
              <button onClick={sendCallback} disabled={msgLoading} className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {msgLoading ? "Sending…" : "Send Callback"}
              </button>
            </div>
            <ResponseBlock resp={msgResp} />
          </SectionCard>

          <SectionCard title="messageFromCo Log" defaultOpen>
            <p className="mb-3 text-xs text-muted-foreground">
               <code>GET /tcc-co-api/api/tcc/messageLog</code> — Shows all received messageFromCo callbacks.
            </p>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{msgLog.length} entries</span>
              <button onClick={fetchMsgLog} disabled={msgLogLoading} className="rounded border border-border px-3 py-1 text-xs hover:bg-accent disabled:opacity-50">
                {msgLogLoading ? "Loading…" : "Refresh"}
              </button>
            </div>
            {msgLog.length === 0 ? (
              <p className="text-sm text-muted-foreground">No callbacks received yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="pb-2 pr-3">Time</th>
                      <th className="pb-2 pr-3">Pack ID</th>
                      <th className="pb-2 pr-3">Status</th>
                      <th className="pb-2 pr-3">Approve</th>
                      <th className="pb-2">Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {msgLog.map((entry: any, i) => (
                      <tr key={i} className="border-b border-border/50 last:border-0">
                        <td className="py-1.5 pr-3 text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</td>
                        <td className="py-1.5 pr-3 font-mono">{entry.payload?.packId}</td>
                        <td className="py-1.5 pr-3">{entry.payload?.rqStatus}</td>
                        <td className="py-1.5 pr-3"><span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${entry.payload?.approveType === "Y" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"}`}>{entry.payload?.approveType}</span></td>
                        <td className="py-1.5 text-[11px] max-w-[200px] truncate">{entry.payload?.remark || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>
      )}
    </div>
  );
}
